import { Button, Card, Stack, Typography, useTheme } from '@mui/material';
import { useMutation, useQuery } from '@tanstack/react-query';
import { AxiosResponse } from 'axios';
import { useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import { organizationAvailabilityApi } from 'src/api/organization-availability';
import { userApi } from 'src/api/user';
import { useUserContext } from 'src/auth/useUserContext';
import CalendarSchedule from 'src/components/calendar/calendar-schedule';
import CalendarScheduleSkeleton from 'src/components/calendar/calendar-schedule-skeleton';
import Iconify from 'src/components/iconify';
import { translateKey } from 'src/locales/translate';
import { useOrganizationContext } from 'src/sections/@dashboard/organization/hooks/useOrganizationContext';
import ScheduleConflictWarningModal from 'src/sections/@dashboard/organization/components/ScheduleConflictWarningModal';
import TimeOffModal from 'src/sections/@dashboard/visit/components/TimeOffModal';
import { ConflictingVisitDto, UpdateAvailabilityDto } from 'woofwoof-api';


export default function OrganizationProfessionalTabsSchedulePage() {
  const { professionalId, organizationId: organizationIdParam } = useParams<{
    professionalId: string;
    organizationId: string;
  }>();
  const { selectedOrganization, isOrganizationAdminRoleSelected } =
    useOrganizationContext();
  const { isOrganizationOwnerRole } = useUserContext();
  const theme = useTheme();
  const [isTimeOffModalOpen, setIsTimeOffModalOpen] = useState(false);
  const [conflictModalOpen, setConflictModalOpen] = useState(false);
  const [conflictingVisits, setConflictingVisits] = useState<ConflictingVisitDto[]>([]);
  const [isConfirming, setIsConfirming] = useState(false);
  const pendingChangeRef = useRef<{
    apply: () => Promise<AxiosResponse<any>>;
    resolve: (value: any) => void;
    reject: (reason?: any) => void;
  } | null>(null);

  const canEditSchedule =
    isOrganizationOwnerRole || isOrganizationAdminRoleSelected;

  // Use URL param if available, otherwise use selected organization from context
  const organizationId = organizationIdParam || selectedOrganization?.id;

  // Fetch professional details
  const { data: professionalData } = useQuery({
    queryKey: [
      'organizationProfessionalDetails',
      organizationId,
      professionalId,
    ],
    queryFn: () => {
      return userApi.userControllerGetOrganizationProfessionalDetails({
        organizationId: organizationId!,
        userId: professionalId!,
      });
    },
    enabled: !!organizationId && !!professionalId,
    onError: (error) => {
      console.error('Error fetching professional data:', error);
    },
  });

  const {
    data: organizationAvailabilityData,
    isLoading,
    refetch,
  } = useQuery({
    queryKey: ['organizationAvailability', organizationId, professionalId],
    queryFn: () => {
      return organizationAvailabilityApi.organizationAvailabilityControllerGetOrganizationAvailability(
        {
          organizationId: organizationId!,
          professionalId: professionalId!,
        },
      );
    },
    enabled: !!organizationId && !!professionalId,
    onError: (err) => {
      console.error('Error fetching availability:', err);
    },
  });

  const {
    mutateAsync: mutateAsyncAvailability,
    isLoading: isAvailabilityLoading,
  } = useMutation({
    mutationFn: ({
      availabilitySlots,
      dryRun,
    }: {
      availabilitySlots: any[];
      dryRun?: boolean;
    }) =>
      organizationAvailabilityApi.organizationAvailabilityControllerSetOrganizationAvailability(
        {
          organizationId: organizationId!,
          professionalId: professionalId!,
          setOrganizationAvailabilityDto: {
            availabilitySlots,
            dryRun,
          } as UpdateAvailabilityDto,
        },
      ),
  });

  // Dry-run preview → confirm modal on conflicts → apply only on Potwierdź;
  // otherwise apply immediately. Resolves `{ cancelled: true }` if backed out.
  const onSave = (availabilitySlots: any[]): Promise<any> =>
    new Promise((resolve, reject) => {
      mutateAsyncAvailability({ availabilitySlots, dryRun: true })
        .then((pre) => {
          const conflicts = pre.data?.conflictingVisits ?? [];
          if (conflicts.length === 0) {
            mutateAsyncAvailability({ availabilitySlots, dryRun: false }).then(
              resolve,
              reject,
            );
            return;
          }
          setConflictingVisits(conflicts);
          pendingChangeRef.current = {
            apply: () =>
              mutateAsyncAvailability({ availabilitySlots, dryRun: false }),
            resolve,
            reject,
          };
          setConflictModalOpen(true);
        })
        .catch(reject);
    });

  const handleConfirmChange = async () => {
    const pending = pendingChangeRef.current;
    if (!pending) return;
    setIsConfirming(true);
    try {
      pending.resolve(await pending.apply());
    } catch (error) {
      pending.reject(error);
    } finally {
      setIsConfirming(false);
      setConflictModalOpen(false);
      pendingChangeRef.current = null;
    }
  };

  const handleCancelChange = () => {
    pendingChangeRef.current?.resolve({ cancelled: true });
    pendingChangeRef.current = null;
    setConflictModalOpen(false);
  };

  const onSaveTimeOff = async (
    timeOffDto: Object[],
  ): Promise<AxiosResponse<any>> => {
    const response =
      await organizationAvailabilityApi.organizationAvailabilityControllerSetOrganizationTimeOff(
        {
          organizationId: organizationId!,
          professionalId: professionalId!,
          setOrganizationTimeOffDto: { timeOff: timeOffDto as any },
        },
      );
    await refetch();
    return response;
  };

  return (
    <Card
      sx={{
        height: '100%',
        display: 'flex',
        flex: 1,
        boxShadow: theme.customShadows.z24,
      }}
    >
      <Stack sx={{ height: 1, minHeight: '560px', flex: 1 }}>
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
          p={3}
          pb={3}
        >
          <Typography variant="h6">Grafik specjalisty</Typography>
          {canEditSchedule && (
            <Button
              size="small"
              variant="contained"
              sx={{
                background: theme.palette.common.black,
                color: theme.palette.common.white,
              }}
              data-testid="schedule-timeoff-open"
              startIcon={<Iconify icon="mdi:calendar-remove" />}
              onClick={() => setIsTimeOffModalOpen(true)}
            >{translateKey("calendar.addTimeOff")}</Button>
          )}
        </Stack>
        {isLoading ? (
          <CalendarScheduleSkeleton />
        ) : organizationAvailabilityData ? (
          <CalendarSchedule
            data={
              (organizationAvailabilityData?.data as any)?.availabilitySlots ||
              []
            }
            timeOff={(organizationAvailabilityData?.data as any)?.timeOff || []}
            refetch={refetch}
            onSave={onSave}
            isLoading={isLoading || isAvailabilityLoading}
            isReadonly={!canEditSchedule}
          />
        ) : null}
      </Stack>
      <TimeOffModal
        isOpen={isTimeOffModalOpen}
        onClose={() => setIsTimeOffModalOpen(false)}
        timeOff={(organizationAvailabilityData?.data as any)?.timeOff || []}
        refetch={refetch}
        onSave={onSaveTimeOff}
        professionalId={professionalId}
        professionalName={
          professionalData?.data.firstName && professionalData?.data.lastName
            ? `${professionalData.data.firstName} ${professionalData.data.lastName}`
            : ''
        }
        hideExistingPeriods
      />
      <ScheduleConflictWarningModal
        open={conflictModalOpen}
        onClose={handleCancelChange}
        onConfirm={handleConfirmChange}
        isConfirming={isConfirming}
        conflicts={conflictingVisits}
      />
    </Card>
  );
}
